import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Card, CardContent } from '@/components/atoms/card';
import { publicGet } from '@/lib/api/publicClient';
import { ApiError } from '@/lib/api/errors';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

export const Route = createFileRoute('/(public)/s/$code')({
  loader: async ({ params }) => {
    try {
      const { targetUrl } = await resolveShortLink(params.code);
      return {
        targetUrl: targetUrl || null,
        error: targetUrl ? null : 'This link is invalid.',
      };
    } catch (err) {
      return {
        targetUrl: null,
        error:
          err instanceof ApiError && err.status === 410
            ? 'This link has already been used.'
            : 'This link has expired or is no longer valid.',
      };
    }
  },
  component: ShortLinkRedirectPage,
});

const shortLinkResolutions = new Map<string, Promise<{ targetUrl: string }>>();

function resolveShortLink(code: string) {
  const existing = shortLinkResolutions.get(code);
  if (existing) return existing;
  const resolution = publicGet<{ targetUrl: string }>(
    `/short-links/${encodeURIComponent(code)}`,
  ).finally(() => shortLinkResolutions.delete(code));
  shortLinkResolutions.set(code, resolution);
  return resolution;
}

/**
 * Resolves a short `/s/{code}` link and redirects to its destination.
 *
 * The operations backend stores the long target URL behind the code; this
 * page fetches it and `window.location.replace`s so the short link never
 * leaves a history entry. Mirrors how the other public pages call the
 * operations host via `publicGet`.
 */
function ShortLinkRedirectPage() {
  const { targetUrl, error } = Route.useLoaderData();

  useEffect(() => {
    if (targetUrl) window.location.replace(targetUrl);
  }, [targetUrl]);

  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-md">
        <CardContent className="py-10">
          {error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Link unavailable</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p>Taking you there…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
